<script>
    import { onMount } from "svelte";
    import Skeleton from "../../../reusable-components/Skeleton.svelte";
    import Orcloader from "./Orcloader.svelte";
    import { navigate } from "svelte-routing";
    import { show_sidebar, active_breadcrumb } from "../../../app-store"

    export let params;
    let loadingState = "preparing"; // preparing, drafting, reviewing, retrying
    let errorMsg = "";
    let isError = false;
    let selected_article_ids = history.state?.selected_article_ids || [];

    onMount(async () => {
        app.body_spinner.remove();

        active_breadcrumb.set("breadcrumb-article-content");
        app.chat_manager.show();

        await app.lucep_ai
            .draft_content(params.draft_refcode, {
                article_ids: selected_article_ids,
                onFinished: (res) => {
                    if (res.status === "ok") {
                        navigate(`/article-content/${params.project_ref}/${params.draft_refcode}`);
                    }
                },
                onUpdate: (res) => {
                    loadingState = res.status;
                },
                onError: (res) => {
                    isError = true;
                    loadingState = "";
                    errorMsg =
                        "We've reached our maximum number of retry attempts due to continued high demand. Please try your request again in a few minutes when our systems have had a chance to recover. We apologize for the inconvenience and appreciate your understanding.";
                },
            })
            .catch((err) => {
                console.error(err);
                isError = true;
                errorMsg = `[${err.status_code}] ${err.msg}`;
            });
    });
</script>

<div class="app-screen article-content-loader article-content-loader-screen">
    <div id="article-content-loader" class="w-100">
        <div class="action-area action-area-article-content-loader">
            <div class="selected-article-list">
                <div class="fs-5 py-3">Selected Articles:</div>
                <Skeleton rows={3} width="100%" height="250px" className="mb-3" />
            </div>
            <div id="blog-editor" class="blog-editor editor-container">
                <div class="editor-title-container p-3">
                    <div class="fs-5">Review Your Draft:</div>
                </div>
                {#if isError}
                    <div class="alert alert-danger m-2" role="alert">
                        <strong>Error:</strong>
                        {errorMsg}
                        <div>
                            <button
                                class="btn btn-primary mt-2"
                                on:click={() => {
                                    navigate(`/article-list/${params.project_ref}/${params.draft_refcode}`);
                                }}
                            >
                                Reload
                            </button>
                        </div>
                    </div>
                {/if}
                <div class="editor-content {loadingState}">
                    {#if loadingState === "preparing"}
                        <div class="editor-content-inner">
                            <div>Preparing your draft...</div>
                        </div>
                    {/if}
                    <Orcloader show={loadingState === "reviewing" && !isError} />
                    {#if (loadingState === "drafting" || loadingState === "reviewing") && !isError}
                        <div class="mb-3 {loadingState === 'drafting' ? '' : 'd-none'}">Drafting...</div>
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={0} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={5} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={10} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={15} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={20} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={25} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={30} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={35} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={40} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={45} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={50} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={55} />
                        <Skeleton rows={1} className="mb-1" hasTransition={true} transitionDelay={60} />
                    {/if}
                    {#if loadingState === "retrying" && !isError}
                        <div class="d-flex flex-column justify-content-center align-items-center h-100">
                            <div class="mb-2 text-danger fw-bold fs-5">Attempting to rewrite your draft...</div>
                            <div class="mb-2 text-muted">Please wait while we try again.</div>
                            <div class="retrying-animation mt-3">
                                <div class="dot dot1"></div>
                                <div class="dot dot2"></div>
                                <div class="dot dot3"></div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
            <div id="article-content-bottom-bar" class="article-bottom-bar p-3 mt-4">
                <span class="text-muted small">Keyprhases</span>
                <div class="d-flex align-items-center gap-1 mb-2">
                    <Skeleton rows={3} width="100%" height="20px" className="mb-3" />
                </div>
                <div class="competitor-urls d-flex gap-1 mb-3 flex-column">
                    <span class="text-muted small">Competitor URLs</span>
                    <div class="d-flex align-items-center gap-1 mb-2">
                        <Skeleton rows={3} width="100%" height="20px" className="mb-3" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    /* https://codepen.io/samratambadekar/pen/ExKYLPg */
    .editor-content {
        --scan-grid-color: 0, 255, 255;
        position: relative;
        width: 100%;
        height: 769px;
        margin: auto;
        pointer-events: none;
    }
    .editor-content-inner {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: #fff;
        overflow: auto;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 1rem;
    }

    .editor-content.preparing .editor-content-inner::after {
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-size: 30px 30px;

        background-image: radial-gradient(rgba(var(--scan-grid-color), 0.25) 2px, transparent 2px),
            linear-gradient(to right, rgba(var(--scan-grid-color), 0.3) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(var(--scan-grid-color), 0.3) 1px, transparent 1px);
        background-position: 15.5px 15.5px;
        opacity: 0;
        animation: gridColorChange 2.5s 0.5s linear infinite alternate;
    }
    .editor-content.preparing:before,
    .editor-content.preparing:after,
    .editor-content.preparing .editor-content-inner:after {
        display: block;
        content: "";
        position: absolute;
        pointer-events: none;
        z-index: 10;
    }
    .editor-content.preparing:before {
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-size: 200px 30px;
        background-image: linear-gradient(to bottom, rgba(var(--scan-grid-color), 0.5) 1px, transparent 1px);
        animation: animateGridHorizontal 5s 1.25s ease-out infinite;
        background-repeat: repeat-y;
        background-position-x: -200px;
    }
    .editor-content.preparing:after {
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background-size: 30px 400px;
        background-image: linear-gradient(to right, rgba(var(--scan-grid-color), 0.5) 1px, transparent 1px);
        animation: animateGridVertical 5s 1s ease-out infinite;
        background-repeat: repeat-x;
        background-position-y: -400px;
    }

    @keyframes animateGridHorizontal {
        from {
            background-position-x: -200px;
        }
        to {
            background-position-x: calc(100% + 200px);
        }
    }
    @keyframes animateGridVertical {
        from {
            background-position-y: -400px;
        }
        to {
            background-position-y: calc(100% + 400px);
        }
    }
    @keyframes gridColorChange {
        100% {
            opacity: 1;
        }
    }

    .retrying-animation {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        margin-top: 1rem;
    }
    .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #7f5700;
        opacity: 0.7;
        animation: bounce 1.2s infinite;
    }
    .dot1 {
        animation-delay: 0s;
    }
    .dot2 {
        animation-delay: 0.2s;
    }
    .dot3 {
        animation-delay: 0.4s;
    }

    @keyframes bounce {
        0%,
        80%,
        100% {
            transform: scale(0.8);
            opacity: 0.7;
        }
        40% {
            transform: scale(1.3);
            opacity: 1;
        }
    }
</style>
